import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { Autocomplete } from '@material-ui/lab'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  FiltroPainelPendenciaBacen,
  PainelPendenciaBacenItem,
  PainelPendenciaBacenResponse,
  PainelPendenciaBacenTotalizadores,
  TenantPainelPendenciaBacen,
  exportarPainelPendenciaBacen,
  obterPainelPendenciaBacen,
  obterTenantsPainelPendenciaBacen,
  reverificarPendenciaBacen
} from 'src/api/recebedor-pendencia-bacen'
import Button from 'src/components/ui/Button'
import { SynRoutes } from 'src/core/consts/synRoutes'
import {
  showNotification,
  showNotificationErrorAPI
} from 'src/core/helpers/notification'
import { saveAs } from 'file-saver'
import TabelaPainelBacen from './tabelaPainel'
import RegularizacaoBacenPage from 'src/pages/dashboard/regularizacao-bacen'
import AvisoBacen from 'src/pages/dashboard/regularizacao-bacen/avisoBacen'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2)
  },
  cabecalho: {
    marginBottom: theme.spacing(1)
  },
  filtros: {
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& .MuiInputLabel-outlined': {
      whiteSpace: 'nowrap'
    }
  },
  buscaComAcao: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    minWidth: 0,
    '& > button': {
      flexShrink: 0
    }
  },
  buscaCampo: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    '& .MuiOutlinedInput-root': {
      overflow: 'hidden'
    },
    '& .MuiOutlinedInput-input': {
      textOverflow: 'ellipsis'
    },
    '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
      maxWidth: 'calc(100% - 28px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  totalizadores: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  legenda: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    opacity: 0.75
  },
  itemLegenda: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    border: 'none',
    borderRadius: 0,
    padding: 0,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    font: 'inherit',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
      opacity: 1
    }
  },
  itemLegendaAtivo: {
    color: theme.palette.primary.main,
    opacity: 1,
    textDecoration: 'underline',
    textUnderlineOffset: 2
  },
  amostraLegenda: {
    width: 10,
    height: 10,
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 1,
    display: 'inline-block',
    flexShrink: 0
  },
  amostraAmarela: {
    width: 4,
    height: 12,
    border: 'none',
    borderRadius: 0,
    backgroundColor: theme.palette.warning.main
  },
  amostraBranca: {
    width: 4,
    height: 12,
    border: 'none',
    borderRadius: 0,
    backgroundColor: '#64B5F6'
  },
  acoes: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  }
}))

const totalizadoresVazio: PainelPendenciaBacenTotalizadores = {
  pendentes: 0,
  regularizados: 0,
  recusados: 0,
  falhas: 0,
  foraDeEscopo: 0
}

const CHIPS_SITUACAO: Array<{
  valor: number
  rotulo: string
  chave: keyof PainelPendenciaBacenTotalizadores
}> = [
  { valor: 1, rotulo: 'Pendentes', chave: 'pendentes' },
  { valor: 2, rotulo: 'Regularizados', chave: 'regularizados' },
  { valor: 3, rotulo: 'Recusados', chave: 'recusados' },
  { valor: 4, rotulo: 'Falhas', chave: 'falhas' },
  { valor: 5, rotulo: 'Fora de escopo', chave: 'foraDeEscopo' }
]

const TIPO_HOSPITAL = 'P:Hospital'
const TIPO_OPME = 'P:OPME'

function valorSelectTipo(filtro: FiltroPainelPendenciaBacen) {
  const tipo = filtro.tipoRecebedor?.[0] || ''
  const subtipo = filtro.subtipoParceiro?.[0] || ''
  if (tipo === 'P' && subtipo === 'Hospital') return TIPO_HOSPITAL
  if (tipo === 'P' && subtipo === 'OPME') return TIPO_OPME
  return tipo
}

function aplicarSelectTipo(
  valor: string
): Pick<FiltroPainelPendenciaBacen, 'tipoRecebedor' | 'subtipoParceiro'> {
  if (valor === TIPO_HOSPITAL) {
    return { tipoRecebedor: ['P'], subtipoParceiro: ['Hospital'] }
  }
  if (valor === TIPO_OPME) {
    return { tipoRecebedor: ['P'], subtipoParceiro: ['OPME'] }
  }
  return {
    tipoRecebedor: valor ? [valor] : undefined,
    subtipoParceiro: undefined
  }
}

function lerQuery(search: string): FiltroPainelPendenciaBacen {
  const params = new URLSearchParams(search)
  const situacao = params.get('situacao')
  const tipo = params.get('tipoRecebedor')
  const subtipo = params.get('subtipoParceiro')
  const tipoPessoa = params.get('tipoPessoa')
  const requerAcaoSyn = params.get('requerAcaoSyn')
  const recebendoRecentemente = params.get('recebendoRecentemente')
  const temPagamentos2025 = params.get('temPagamentos2025')
  const temPagamentos2026 = params.get('temPagamentos2026')

  function lerBool(valor: string | null): boolean | undefined {
    if (valor === 'true') return true
    if (valor === 'false') return false
    return undefined
  }

  return {
    situacao: situacao
      ? situacao.split(',').map(Number).filter(n => !Number.isNaN(n))
      : undefined,
    tipoRecebedor: tipo ? tipo.split(',').filter(Boolean) : undefined,
    subtipoParceiro: subtipo ? subtipo.split(',').filter(Boolean) : undefined,
    tenantId: params.get('tenantId') || undefined,
    busca: params.get('busca') || undefined,
    tipoPessoa: tipoPessoa === 'PF' || tipoPessoa === 'PJ' ? tipoPessoa : undefined,
    requerAcaoSyn: lerBool(requerAcaoSyn),
    recebendoRecentemente: lerBool(recebendoRecentemente),
    temPagamentos2025: lerBool(temPagamentos2025),
    temPagamentos2026: lerBool(temPagamentos2026),
    pagina: Number(params.get('pagina') || 1),
    tamanhoPagina: 12
  }
}

function gravarQuery(filtro: FiltroPainelPendenciaBacen) {
  const params = new URLSearchParams()
  if (filtro.situacao?.length) {
    params.set('situacao', filtro.situacao.join(','))
  }
  if (filtro.tipoRecebedor?.length) {
    params.set('tipoRecebedor', filtro.tipoRecebedor.join(','))
  }
  if (filtro.subtipoParceiro?.length) {
    params.set('subtipoParceiro', filtro.subtipoParceiro.join(','))
  }
  if (filtro.tenantId) {
    params.set('tenantId', filtro.tenantId)
  }
  if (filtro.busca) {
    params.set('busca', filtro.busca)
  }
  if (filtro.tipoPessoa) {
    params.set('tipoPessoa', filtro.tipoPessoa)
  }
  if (typeof filtro.requerAcaoSyn === 'boolean') {
    params.set('requerAcaoSyn', String(filtro.requerAcaoSyn))
  }
  if (typeof filtro.recebendoRecentemente === 'boolean') {
    params.set('recebendoRecentemente', String(filtro.recebendoRecentemente))
  }
  if (typeof filtro.temPagamentos2025 === 'boolean') {
    params.set('temPagamentos2025', String(filtro.temPagamentos2025))
  }
  if (typeof filtro.temPagamentos2026 === 'boolean') {
    params.set('temPagamentos2026', String(filtro.temPagamentos2026))
  }
  if (filtro.pagina && filtro.pagina > 1) {
    params.set('pagina', String(filtro.pagina))
  }
  return params.toString()
}

function valorSelectBool(valor?: boolean) {
  if (valor === true) return 'true'
  if (valor === false) return 'false'
  return ''
}

function lerSelectBool(valor: string): boolean | undefined {
  if (valor === 'true') return true
  if (valor === 'false') return false
  return undefined
}

export default function PainelRegularizacaoBacenPage() {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const pedidoRef = useRef(0)

  const [filtro, setFiltro] = useState<FiltroPainelPendenciaBacen>(() =>
    lerQuery(location.search)
  )
  const [buscaLocal, setBuscaLocal] = useState(filtro.busca || '')
  const [carregando, setCarregando] = useState(true)
  const [exportando, setExportando] = useState(false)
  const [resposta, setResposta] = useState<PainelPendenciaBacenResponse | null>(
    null
  )
  const [reverificandoId, setReverificandoId] = useState<string | null>(null)
  const [statusReverificacao, setStatusReverificacao] = useState('')
  const [casoAberto, setCasoAberto] = useState<string | null>(null)
  const [tenants, setTenants] = useState<TenantPainelPendenciaBacen[]>([])
  const [carregandoTenants, setCarregandoTenants] = useState(false)

  useEffect(() => {
    let ativo = true
    setCarregandoTenants(true)
    obterTenantsPainelPendenciaBacen()
      .then(respostaTenants => {
        if (ativo) setTenants(respostaTenants.data || [])
      })
      .catch(erro => {
        if (ativo) showNotificationErrorAPI(erro)
      })
      .finally(() => {
        if (ativo) setCarregandoTenants(false)
      })
    return () => {
      ativo = false
    }
  }, [])

  useEffect(() => {
    const query = gravarQuery(filtro)
    if (query !== location.search.replace(/^\?/, '')) {
      history.replace({
        pathname: SynRoutes.painelRegularizacaoBacen,
        search: query ? `?${query}` : ''
      })
    }
  }, [filtro, history, location.search])

  useEffect(() => {
    let ativo = true
    const pedido = ++pedidoRef.current

    async function carregar() {
      setCarregando(true)
      try {
        const { data } = await obterPainelPendenciaBacen(filtro)
        if (!ativo || pedido !== pedidoRef.current) return
        setResposta(data)
      } catch (erro) {
        if (!ativo || pedido !== pedidoRef.current) return
        showNotificationErrorAPI(erro)
        setResposta(null)
      } finally {
        if (ativo && pedido === pedidoRef.current) setCarregando(false)
      }
    }

    carregar()
    return () => {
      ativo = false
    }
  }, [filtro])

  const totalizadores = resposta?.totalizadores || totalizadoresVazio
  const itens = resposta?.itens || []
  const total = resposta?.total || 0
  const pagina = filtro.pagina || 1
  const tamanho = filtro.tamanhoPagina || 12
  const totalPaginas = Math.max(1, Math.ceil(total / tamanho))
  const situacaoAtiva = filtro.situacao?.[0]

  function aplicarFiltroSituacao(valor?: number) {
    setFiltro(atual => ({
      ...atual,
      pagina: 1,
      situacao:
        valor === undefined || atual.situacao?.[0] === valor
          ? undefined
          : [valor]
    }))
  }

  function aplicarFiltroRequerAcaoSyn(valor?: boolean) {
    setFiltro(atual => ({
      ...atual,
      pagina: 1,
      requerAcaoSyn:
        valor === undefined || atual.requerAcaoSyn === valor
          ? undefined
          : valor
    }))
  }

  function valorFiltroRequerAcaoSyn() {
    return valorSelectBool(filtro.requerAcaoSyn)
  }

  async function handleReverificar(item: PainelPendenciaBacenItem) {
    setReverificandoId(item.pendenciaId)
    setStatusReverificacao(`Reverificando ${item.nome}…`)
    try {
      const { data } = await reverificarPendenciaBacen(item.pendenciaId)
      setResposta(atual => {
        if (!atual) return atual
        const itensAtualizados = atual.itens.map(i =>
          i.pendenciaId === data.pendenciaId ? data : i
        )
        return { ...atual, itens: itensAtualizados }
      })
      setStatusReverificacao(
        `${item.nome} atualizado para situação ${data.situacao}.`
      )
      showNotification('Sucesso', 'Caso reavaliado.', 'success', true)

      const { data: painel } = await obterPainelPendenciaBacen(filtro)
      setResposta(painel)
    } catch (erro) {
      showNotificationErrorAPI(erro)
      setStatusReverificacao(`Falha ao reverificar ${item.nome}.`)
    } finally {
      setReverificandoId(null)
    }
  }

  async function handleExportar() {
    setExportando(true)
    try {
      const response = await exportarPainelPendenciaBacen(filtro)
      const data = response.data
      const contentType = String(response.headers?.['content-type'] || '')

      // Com responseType: 'blob', erro JSON da API também chega como Blob.
      if (contentType.includes('application/json') || contentType.includes('text/')) {
        const texto = await data.text()
        let mensagem = 'Falha ao exportar o painel BACEN.'
        try {
          const json = JSON.parse(texto)
          mensagem =
            json?.errors?.[0] ||
            json?.message ||
            json?.title ||
            mensagem
        } catch {
          if (texto) mensagem = texto
        }
        throw new Error(mensagem)
      }

      const blob =
        data instanceof Blob
          ? data
          : new Blob([data], {
              type:
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            })

      if (!blob.size) {
        throw new Error(
          'A exportação retornou arquivo vazio. Tente novamente ou filtre menos registros.'
        )
      }

      // file-saver evita o bug do Chrome de arquivo 0 bytes ao dar
      // revokeObjectURL imediatamente após link.click().
      saveAs(blob, 'painel-pendencia-bacen.xlsx')
      showNotification('Sucesso', 'Exportação gerada.', 'success', true)
    } catch (erro) {
      showNotificationErrorAPI(erro)
    } finally {
      setExportando(false)
    }
  }

  if (casoAberto) {
    return (
      <RegularizacaoBacenPage
        modoPainel
        pendenciaIdInicial={casoAberto}
        onVoltarPainel={() => {
          setCasoAberto(null)
          setFiltro(atual => ({ ...atual }))
        }}
      />
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.cabecalho}>
        <Typography variant="h5" component="h1" gutterBottom>
          Regularização BACEN — painel
        </Typography>
        {resposta ? (
          <AvisoBacen
            variante="painel"
            dataLimite={resposta.dataLimite}
            bloqueioAtivo={resposta.bloqueioAtivo}
          />
        ) : null}
      </Box>

      <Box className={classes.filtros}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="filtro-situacao-label">Situação</InputLabel>
              <Select
                labelId="filtro-situacao-label"
                label="Situação"
                value={filtro.situacao?.[0] || ''}
                onChange={e => {
                  const valor = e.target.value
                  setFiltro(atual => ({
                    ...atual,
                    pagina: 1,
                    situacao: valor === '' ? undefined : [Number(valor)]
                  }))
                }}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value={1}>Pendente</MenuItem>
                <MenuItem value={2}>Regularizado</MenuItem>
                <MenuItem value={3}>Recusado</MenuItem>
                <MenuItem value={4}>Falha</MenuItem>
                <MenuItem value={5}>Fora de escopo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="filtro-tipo-label">Tipo</InputLabel>
              <Select
                labelId="filtro-tipo-label"
                label="Tipo"
                value={valorSelectTipo(filtro)}
                onChange={e => {
                  const valor = String(e.target.value)
                  setFiltro(atual => ({
                    ...atual,
                    pagina: 1,
                    ...aplicarSelectTipo(valor)
                  }))
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="M">Médico</MenuItem>
                <MenuItem value="ME">Membro de equipe</MenuItem>
                <MenuItem value={TIPO_HOSPITAL}>Hospital/Clínica</MenuItem>
                <MenuItem value={TIPO_OPME}>Fornecedor de OPME</MenuItem>
                <MenuItem value="FA">Fornecedor anestesia</MenuItem>
                <MenuItem value="FS">Fornecedor bolsa de sangue</MenuItem>
                <MenuItem value="SYN">Syn</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="filtro-pfpj-label">PF/PJ</InputLabel>
              <Select
                labelId="filtro-pfpj-label"
                label="PF/PJ"
                value={filtro.tipoPessoa || ''}
                onChange={e => {
                  const valor = String(e.target.value)
                  setFiltro(atual => ({
                    ...atual,
                    pagina: 1,
                    tipoPessoa:
                      valor === 'PF' || valor === 'PJ' ? valor : undefined
                  }))
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="PF">Pessoa física (PF)</MenuItem>
                <MenuItem value="PJ">Pessoa jurídica (PJ)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="filtro-acao-label">Ação</InputLabel>
              <Select
                labelId="filtro-acao-label"
                label="Ação"
                value={valorFiltroRequerAcaoSyn()}
                onChange={e => {
                  setFiltro(atual => ({
                    ...atual,
                    pagina: 1,
                    requerAcaoSyn: lerSelectBool(String(e.target.value))
                  }))
                }}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="true">Ação Syn</MenuItem>
                <MenuItem value="false">Na plataforma</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              size="small"
              options={tenants}
              loading={carregandoTenants}
              value={tenants.find(t => t.id === filtro.tenantId) || null}
              getOptionLabel={option => option?.descricao || ''}
              getOptionSelected={(option, value) => option?.id === value?.id}
              onChange={(_e, newValue: TenantPainelPendenciaBacen | null) => {
                setFiltro(atual => ({
                  ...atual,
                  pagina: 1,
                  tenantId: newValue?.id || undefined
                }))
              }}
              noOptionsText="Nenhum tenant encontrado"
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  label="Tenant"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="filtro-recente-label">
                Recebendo recentemente
              </InputLabel>
              <Select
                labelId="filtro-recente-label"
                label="Recebendo recentemente"
                value={valorSelectBool(filtro.recebendoRecentemente)}
                onChange={e => {
                  setFiltro(atual => ({
                    ...atual,
                    pagina: 1,
                    recebendoRecentemente: lerSelectBool(String(e.target.value))
                  }))
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Sim</MenuItem>
                <MenuItem value="false">Não</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="filtro-pag2025-label">Pag. 2025</InputLabel>
              <Select
                labelId="filtro-pag2025-label"
                label="Pag. 2025"
                value={valorSelectBool(filtro.temPagamentos2025)}
                onChange={e => {
                  setFiltro(atual => ({
                    ...atual,
                    pagina: 1,
                    temPagamentos2025: lerSelectBool(String(e.target.value))
                  }))
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Com pagamentos</MenuItem>
                <MenuItem value="false">Sem pagamentos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="filtro-pag2026-label">Pag. 2026</InputLabel>
              <Select
                labelId="filtro-pag2026-label"
                label="Pag. 2026"
                value={valorSelectBool(filtro.temPagamentos2026)}
                onChange={e => {
                  setFiltro(atual => ({
                    ...atual,
                    pagina: 1,
                    temPagamentos2026: lerSelectBool(String(e.target.value))
                  }))
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Com pagamentos</MenuItem>
                <MenuItem value="false">Sem pagamentos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box className={classes.buscaComAcao}>
              <TextField
                className={classes.buscaCampo}
                fullWidth
                size="small"
                variant="outlined"
                label="Busca"
                placeholder="Nome ou documento"
                InputLabelProps={{ shrink: true }}
                value={buscaLocal}
                onChange={e => setBuscaLocal(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    setFiltro(atual => ({
                      ...atual,
                      pagina: 1,
                      busca: buscaLocal.trim() || undefined
                    }))
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  setFiltro(atual => ({
                    ...atual,
                    pagina: 1,
                    busca: buscaLocal.trim() || undefined
                  }))
                }
              >
                Filtrar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        className={classes.totalizadores}
        aria-label="Totalizadores por situação"
      >
        {CHIPS_SITUACAO.map(chip => {
          const selecionado = situacaoAtiva === chip.valor
          return (
            <Chip
              key={chip.valor}
              clickable
              color={selecionado ? 'primary' : 'default'}
              variant={selecionado ? 'default' : 'outlined'}
              label={`${chip.rotulo}: ${totalizadores[chip.chave]}`}
              onClick={() => aplicarFiltroSituacao(chip.valor)}
              aria-pressed={selecionado}
            />
          )
        })}
        <Chip
          clickable
          variant="outlined"
          label={`Total filtro: ${total}`}
          onClick={() => aplicarFiltroSituacao()}
          aria-label="Limpar filtro de situação"
        />
      </Box>

      <Box className={classes.legenda} aria-label="Legenda das cores da tabela">
        <Typography variant="caption" color="textSecondary" component="span">
          Legenda:
        </Typography>
        <button
          type="button"
          className={`${classes.itemLegenda} ${
            filtro.requerAcaoSyn === true ? classes.itemLegendaAtivo : ''
          }`}
          onClick={() => aplicarFiltroRequerAcaoSyn(true)}
          aria-pressed={filtro.requerAcaoSyn === true}
          title="Filtrar recebedores sem acesso à plataforma"
        >
          <span
            className={`${classes.amostraLegenda} ${classes.amostraAmarela}`}
          />
          <Typography variant="caption" component="span">
            Amarelo — requer ação da Syn (sem login na plataforma)
          </Typography>
        </button>
        <button
          type="button"
          className={`${classes.itemLegenda} ${
            filtro.requerAcaoSyn === false ? classes.itemLegendaAtivo : ''
          }`}
          onClick={() => aplicarFiltroRequerAcaoSyn(false)}
          aria-pressed={filtro.requerAcaoSyn === false}
          title="Filtrar casos em que o seller pode regularizar na plataforma"
        >
          <span
            className={`${classes.amostraLegenda} ${classes.amostraBranca}`}
          />
          <Typography variant="caption" component="span">
            Azul — seller pode regularizar na plataforma
          </Typography>
        </button>
      </Box>

      <Box className={classes.acoes}>
        <Button
          variant="outlined"
          color="primary"
          disabled={exportando}
          onClick={handleExportar}
        >
          {exportando ? 'Exportando…' : 'Exportar .xlsx'}
        </Button>
      </Box>

      <div
        role="status"
        aria-live="polite"
        style={{ position: 'absolute', left: -9999 }}
      >
        {statusReverificacao}
      </div>

      {carregando ? (
        <CircularProgress aria-label="Carregando painel BACEN" />
      ) : itens.length === 0 ? (
        <Alert severity="info">
          Nenhum caso encontrado para o filtro aplicado.
        </Alert>
      ) : (
        <TabelaPainelBacen
          itens={itens}
          reverificandoId={reverificandoId}
          onReverificar={handleReverificar}
          onAbrirCaso={id => setCasoAberto(id)}
        />
      )}

      {totalPaginas > 1 && (
        <Box mt={2} display="flex" style={{ gap: 8 }}>
          <Button
            disabled={pagina <= 1 || carregando}
            onClick={() =>
              setFiltro(atual => ({ ...atual, pagina: pagina - 1 }))
            }
          >
            Anterior
          </Button>
          <Typography variant="body2" style={{ alignSelf: 'center' }}>
            Página {pagina} de {totalPaginas}
          </Typography>
          <Button
            disabled={pagina >= totalPaginas || carregando}
            onClick={() =>
              setFiltro(atual => ({ ...atual, pagina: pagina + 1 }))
            }
          >
            Próxima
          </Button>
        </Box>
      )}
    </Box>
  )
}
